import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import { MultiSelectDropdown } from "../../../components/dropdown/MultiSelectDropdown";
import {
  useHazardousMaterialStore,
  useIncidentSpillStore,
} from "../../../store/hazardousMaterialStore";
import {
  IncidentSeverityLevelsType,
  IncidentSpillReportType,
  OrgUserType,
} from "../../../types/api";
import {
  useIncidentSeverityLevelsStore,
  useLocationStore,
} from "../../../store/masterDataStore";
import { useOrgUsersStore } from "../../../store/store";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgba(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const emptyForm: IncidentSpillReportType = {
  incidentId: undefined, // auto-generated
  dateTime: "",
  location: "",
  materialsInvolved: [],
  severity: "Minor",
  description: "",
  actionsTaken: "",
  reportedBy: "",
  attachments: [],
};

const IncidentAndSpillReports: React.FC = () => {
  const [rowData, setRowData] = useState<IncidentSpillReportType[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState<IncidentSpillReportType>(emptyForm);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState<string | undefined>(undefined);

  const {
    incidentReports,
    loading,
    fetchAllReports,
    createReport,
    updateReport,
    deleteReport,
  } = useIncidentSpillStore();

  const {
    hazardousMaterials,
    loading: hazardousMaterialsLoading,
    fetchAllHazardousMaterials,
  } = useHazardousMaterialStore();
  const {
    incidentSeverityLevels,
    incidentSeverityLevelsMap,
    loading: incidentSeverityLevelsLoading,
    fetchAllIncidentSeverityLevels,
  } = useIncidentSeverityLevelsStore();
  const {
    allLocations,
    allLocationsMap,
    loading: locationsLoading,
    fetchAllLocations,
  } = useLocationStore();
  const {
    orgUsersData,
    orgUsersMap,
    loading: orgUsersLoading,
    fetchOrgUsers,
  } = useOrgUsersStore();

  useEffect(() => {
    fetchAllReports();
    if (!allLocations.length) fetchAllLocations();
    if (!hazardousMaterials.length) fetchAllHazardousMaterials();
    if (!incidentSeverityLevels.length) fetchAllIncidentSeverityLevels();
    if (!orgUsersData.length) fetchOrgUsers();
  }, [
    fetchAllReports,
    fetchAllLocations,
    fetchAllHazardousMaterials,
    fetchAllIncidentSeverityLevels,
    fetchOrgUsers,
  ]);

  useEffect(() => {
    setRowData(incidentReports ?? []);
  }, [incidentReports]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value, options } = e.target as HTMLSelectElement;
    // if (name === "materialsInvolved") {
    //   const selected = Array.from(options)
    //     .filter((opt) => opt.selected)
    //     .map((opt) => opt.value);
    //   setFormData((prev) => ({ ...prev, materialsInvolved: selected }));
    // } else {
    setFormData((prev) => ({ ...prev, [name]: value }));
    // }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData((prev) => ({
        ...prev,
        attachments: [
          ...(prev.attachments || []),
          ...Array.from(e.target.files || []),
        ],
      }));
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments?.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.dateTime ||
      !formData.location ||
      !formData.materialsInvolved.length ||
      !formData.severity ||
      !formData.reportedBy
    ) {
      alert("Please fill all required fields");
      return;
    }

    let success = false;
    if (isEditMode && editId) {
      success = await updateReport(formData, editId);
    } else {
      success = await createReport(formData);
    }

    if (success) {
      setModalOpen(false);
      setFormData(emptyForm);
      setEditId(undefined);
      setIsEditMode(false);
    }
  };

  const handleEdit = (data: IncidentSpillReportType) => {
    setFormData({ ...data });
    setEditId(data._id);
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure to delete this incident report?")) {
      await deleteReport(id);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setFormData(emptyForm);
    setEditId(undefined);
    setIsEditMode(false);
  };

  const columnDefs = useMemo<any[]>(
    () => [
      // {
      //   field: "incidentId",
      //   headerName: "Incident ID",
      //   minWidth: 130,
      //   headerStyle: { backgroundColor: "#e9f5ff" },
      // },
      {
        field: "dateTime",
        headerName: "Date & Time",
        minWidth: 160,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          params.data.dateTime
            ? new Date(params.data.dateTime).toLocaleString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
            : "N/A",
      },
      {
        field: "location",
        headerName: "Location",
        minWidth: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          allLocationsMap.get(params.data.location) ||
          // params.data.location ||
          "N/A",
      },
      {
        field: "severity",
        headerName: "Severity",
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          incidentSeverityLevelsMap.get(params.data.severity) ||
          // params.data.severity ||
          "N/A",
      },
      {
        field: "reportedBy",
        headerName: "Reported By",
        minWidth: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          orgUsersMap.get(params.data.reportedBy) ||
          // params.data.reportedBy ||
          "N/A",
      },
      {
        field: "action",
        headerName: "Action",
        minWidth: 100,
        cellRenderer: (params: any) => (
          <div className="flex gap-3">
            <button
              onClick={() => handleEdit(params.data)}
              title="Edit"
              className="hover:bg-gray-100 rounded p-1"
            >
              <img src={images.editIcon} alt="edit" className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleDelete(params.data._id!)}
              title="Delete"
              className="hover:bg-red-100 rounded p-1"
            >
              <Trash2 className="text-red-500" size={20} />
            </button>
          </div>
        ),
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
    ],
    [
      allLocationsMap,
      orgUsersMap,
      incidentSeverityLevelsMap,
      handleEdit,
      handleDelete,
      images.editIcon,
    ]
  );

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Incident & Spill Reports</h2>
          <button
            onClick={() => {
              setFormData(emptyForm);
              setIsEditMode(false);
              setModalOpen(true);
            }}
            className="flex items-center justify-center gap-2 bg-[#0fa9a6] text-white px-4 py-2 rounded-full hover:bg-teal-600"
          >
            <Plus size={20} /> Add Report
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={{
                    resizable: true,
                    sortable: false,
                    filter: false,
                    editable: false,
                  }}
                  theme={gridTheme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={
                    loading ||
                    hazardousMaterialsLoading ||
                    incidentSeverityLevelsLoading ||
                    locationsLoading ||
                    orgUsersLoading
                  }
                  domLayout="autoHeight"
                  pagination
                  paginationPageSize={5}
                />
              </div>
            ) : (
              <p className="text-center py-20 text-gray-500">
                No incident reports found
              </p>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg max-h-[80vh] flex flex-col relative"
      >
        <div className="pt-6 px-6 flex-1 overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
            {isEditMode ? "Edit Incident Report" : "Add Incident Report"}
          </h2>
          <form
            onSubmit={handleSubmit}
            id="incidentSpillForm"
            className="pb-10 flex flex-col gap-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Date and Time<span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="dateTime"
                  value={formData.dateTime}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Location<span className="text-red-500">*</span>
                </label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="">Select Location</option>
                  {allLocations.map((l) => (
                    <option key={l._id} value={l._id}>
                      {l.room} - {l.floorNumber}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Severity<span className="text-red-500">*</span>
                </label>
                <select
                  name="severity"
                  value={formData.severity}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="">Select Severity</option>
                  {incidentSeverityLevels.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.incidentSeverityLevelName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Reported By<span className="text-red-500">*</span>
                </label>
                <select
                  name="reportedBy"
                  value={formData.reportedBy}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="">Select Reported By</option>
                  {orgUsersData.map((user: OrgUserType) => (
                    <option key={user._id as string} value={user._id as string}>
                      {user.firstName} {user.lastName} - {user.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  Materials Involved<span className="text-red-500">*</span>
                </label>
                <MultiSelectDropdown
                  options={hazardousMaterials.map((m) => ({
                    label: m.materialName,
                    value: m._id || "",
                  }))}
                  selectedValues={formData.materialsInvolved}
                  onChange={(vals) => {
                    setFormData((prev) => ({
                      ...prev,
                      materialsInvolved: vals,
                    }));
                  }}
                  placeholder="Select Materials Involved"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Actions Taken
              </label>
              <textarea
                name="actionsTaken"
                value={formData.actionsTaken}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Attachments (images/PDF)
              </label>
              <input
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-[#375998]"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.attachments?.map((file, i) => (
                  <div
                    key={i}
                    className="flex items-center space-x-2 bg-gray-100 rounded p-1"
                  >
                    <span className="truncate max-w-xs">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(i)}
                      className="text-red-600 font-bold"
                      aria-label={`Remove attachment ${file.name}`}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="h-10" />
          </form>
        </div>
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] rounded-b-lg sticky bottom-0">
          <button
            type="button"
            onClick={handleModalClose}
            className="px-6 py-2 rounded-full bg-[#8497B0] text-white hover:bg-teal-600 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="incidentSpillForm"
            className="px-6 py-2 rounded-full bg-[#00A8A8] text-white hover:bg-teal-600 transition"
          >
            {isEditMode ? "Update" : "Add"} Report
          </button>
        </div>
      </Modal>
    </>
  );
};

export default IncidentAndSpillReports;
